/**
 * Multi-Select Dropdown Interface
 * Allows selecting multiple values from a dropdown with choices
 */

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Choice {
    text: string;
    value: string;
}

interface MultiSelectDropdownProps {
    value: string[] | null;
    onChange: (value: string[] | null) => void;
    choices: Choice[];
    disabled?: boolean;
    placeholder?: string;
    allowOther?: boolean;
    allowNone?: boolean;
    className?: string;
    /** Called when a custom "other" value is added, so the parent can persist it to field settings */
    onAddOtherChoice?: (value: string) => void;
}

export function MultiSelectDropdown({
    value,
    onChange,
    choices,
    disabled = false,
    placeholder = 'Select items...',
    allowOther = false,
    allowNone = false,
    className,
    onAddOtherChoice,
}: MultiSelectDropdownProps) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const selectedValues = useMemo(() => value || [], [value]);

    const handleSelect = (selectedValue: string) => {
        if (selectedValues.includes(selectedValue)) {
            // Deselect
            const newValues = selectedValues.filter((v) => v !== selectedValue);
            onChange(newValues.length > 0 ? newValues : null);
        } else {
            // Select
            onChange([...selectedValues, selectedValue]);
        }
    };

    const handleRemove = (valueToRemove: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const newValues = selectedValues.filter((v) => v !== valueToRemove);
        onChange(newValues.length > 0 ? newValues : null);
    };

    const handleAddOther = () => {
        if (inputValue && !selectedValues.includes(inputValue)) {
            onChange([...selectedValues, inputValue]);
            // Persist the new choice to field settings
            onAddOtherChoice?.(inputValue);
            setInputValue('');
        }
    };

    const getDisplayText = (val: string): string => {
        const choice = choices.find((c) => c.value === val);
        return choice?.text || val;
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        'w-full justify-between font-normal',
                        selectedValues.length === 0 && 'text-muted-foreground',
                        className
                    )}
                >
                    <div className="flex flex-1 flex-wrap gap-1 min-w-0 max-h-24 overflow-y-auto">
                        {selectedValues.length === 0 ? (
                            <span className="truncate">{placeholder}</span>
                        ) : (
                            selectedValues.map((val) => (
                                <Badge
                                    key={val}
                                    variant="secondary"
                                    className="mr-1 min-w-0"
                                >
                                    <span className="truncate block text-left min-w-0 max-w-[400px]">{getDisplayText(val)}</span>
                                    {!disabled && (
                                        <button
                                            type="button"
                                            className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 shrink-0"
                                            onClick={(e) => handleRemove(val, e)}
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    )}
                                </Badge>
                            ))
                        )}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full min-w-[200px] p-2" align="start">
                <Command>
                    <CommandInput
                        placeholder="Search..."
                        value={inputValue}
                        onValueChange={setInputValue}
                    />
                    <CommandList>
                        <CommandEmpty>
                            {allowOther && inputValue ? (
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start"
                                    onClick={handleAddOther}
                                >
                                    Add "{inputValue}"
                                </Button>
                            ) : (
                                'No results found.'
                            )}
                        </CommandEmpty>
                        <CommandGroup>
                            {choices.map((choice) => (
                                <CommandItem
                                    key={choice.value}
                                    value={choice.value}
                                    onSelect={() => handleSelect(choice.value)}
                                    className="px-3 py-2"
                                >
                                    <Check
                                        className={cn(
                                            'mr-2 h-4 w-4 shrink-0',
                                            selectedValues.includes(choice.value)
                                                ? 'opacity-100'
                                                : 'opacity-0'
                                        )}
                                    />
                                    <span className="truncate text-left">{choice.text}</span>
                                </CommandItem>
                            ))}
                            {allowOther && inputValue && (
                                <CommandItem
                                    value={`__add-${inputValue}`}
                                    onSelect={handleAddOther}
                                    className="px-3 py-2"
                                >
                                    <Check className={cn('mr-2 h-4 w-4 opacity-0 shrink-0')} />
                                    <span className="truncate text-left">Add "{inputValue}"</span>
                                </CommandItem>
                            )}
                            {allowNone && selectedValues.length > 0 && (
                                <CommandItem
                                    value="__clear"
                                    onSelect={() => onChange(null)}
                                    className="px-3 py-2"
                                >
                                    <Check className={cn('mr-2 h-4 w-4 opacity-0 shrink-0')} />
                                    <span className="truncate text-left">Clear selection</span>
                                </CommandItem>
                            )}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
